/**
 * Document window layout — pure geometry helpers that translate the store's
 * arrangement state into per-window inline styles. Ported from pik's
 * `components/core/canvas/windowLayout.ts` (the reference implementation).
 */

import type { CSSProperties } from 'react';
import type {
  DocArrangement,
  DocSnapZone,
  DocWindowState,
} from '../types';

export interface WindowLayoutResult {
  style?: CSSProperties;
  interactive: boolean;
}

/** Screen-edge proximity (px) that activates a snap zone during a drag.
 * Overridable per app through the `--doc-snap-threshold` token. */
export const SNAP_EDGE_THRESHOLD = 32;

/** Class list for a snap-zone preview. With a single open document any zone
 * drop re-docks the document fullscreen instead of splitting, so the preview
 * shows the fullscreen rect rather than a half/quarter one. */
export const defaultSnapPreviewClassName = (
  zone: DocSnapZone,
  openDocumentCount: number,
): string =>
  openDocumentCount <= 1
    ? 'doc-snap-preview doc-snap-fullscreen'
    : `doc-snap-preview doc-snap-${zone}`;

/** Half-area rect a window occupies when dropped into the given zone,
 * relative to the float root. */
export const snapZoneGeometry = (
  zone: DocSnapZone,
  areaWidth: number,
  areaHeight: number,
): {
  position: { x: number; y: number };
  size: { width: number; height: number };
} => {
  switch (zone) {
    case 'left':
      return {
        position: { x: 0, y: 0 },
        size: { width: areaWidth / 2, height: areaHeight },
      };
    case 'right':
      return {
        position: { x: areaWidth / 2, y: 0 },
        size: { width: areaWidth / 2, height: areaHeight },
      };
    case 'top':
      return {
        position: { x: 0, y: 0 },
        size: { width: areaWidth, height: areaHeight / 2 },
      };
    case 'bottom':
      return {
        position: { x: 0, y: areaHeight / 2 },
        size: { width: areaWidth, height: areaHeight / 2 },
      };
    case 'top-left':
      return {
        position: { x: 0, y: 0 },
        size: { width: areaWidth / 2, height: areaHeight / 2 },
      };
    case 'top-right':
      return {
        position: { x: areaWidth / 2, y: 0 },
        size: { width: areaWidth / 2, height: areaHeight / 2 },
      };
    case 'bottom-left':
      return {
        position: { x: 0, y: areaHeight / 2 },
        size: { width: areaWidth / 2, height: areaHeight / 2 },
      };
    case 'bottom-right':
      return {
        position: { x: areaWidth / 2, y: areaHeight / 2 },
        size: { width: areaWidth / 2, height: areaHeight / 2 },
      };
  }
};

/** Resolve the snap zone for a pointer position relative to the float root.
 * Corners take precedence over edges. Returns null outside the area or when
 * no edge is within the threshold. */
export const resolveSnapZone = (
  relX: number,
  relY: number,
  areaWidth: number,
  areaHeight: number,
  threshold: number = SNAP_EDGE_THRESHOLD,
): DocSnapZone | null => {
  if (relX < 0 || relY < 0 || relX > areaWidth || relY > areaHeight) {
    return null;
  }
  const nearLeft = relX <= threshold;
  const nearRight = relX >= areaWidth - threshold;
  const nearTop = relY <= threshold;
  const nearBottom = relY >= areaHeight - threshold;
  if (nearLeft && nearTop) return 'top-left';
  if (nearRight && nearTop) return 'top-right';
  if (nearLeft && nearBottom) return 'bottom-left';
  if (nearRight && nearBottom) return 'bottom-right';
  if (nearLeft) return 'left';
  if (nearRight) return 'right';
  if (nearTop) return 'top';
  if (nearBottom) return 'bottom';
  return null;
};

/** Column count for the tile grid: roughly square, at least one */
const tileGridColumns = (count: number): number =>
  Math.max(1, Math.ceil(Math.sqrt(count)));

export const tileGridStyle = (count: number): CSSProperties => {
  const columns = tileGridColumns(count);
  const rows = Math.ceil(count / columns);
  return {
    display: 'grid',
    gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
    gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))`,
    gap: '4px',
    padding: '4px',
  };
};

export const computeWindowLayout = (
  arrangement: DocArrangement,
  index: number,
  windowState: DocWindowState | undefined,
  isActive: boolean,
): WindowLayoutResult => {
  const cascadeOffset = index * 28;

  // A floated window floats above every arrangement: a single document can
  // be torn out of the tab bar (or floated from the Window menu) without
  // disturbing how the remaining documents are arranged. Docked windows in
  // the 'float' arrangement stay hidden until they are floated again.
  if (windowState?.mode === 'float') {
    return {
      style: {
        position: 'absolute',
        inset: 'auto',
        top: windowState.position?.y ?? cascadeOffset,
        left: windowState.position?.x ?? cascadeOffset,
        width: windowState.size?.width ?? '70%',
        height: windowState.size?.height ?? '70%',
        zIndex: isActive ? 40 : 30 - index,
      },
      interactive: true,
    };
  }

  if (arrangement === 'float') {
    return { style: { display: 'none' }, interactive: false };
  }

  switch (arrangement) {
    case 'tile':
      return {
        style: {
          position: 'relative',
          inset: 'auto',
          width: '100%',
          height: '100%',
          minWidth: 0,
          minHeight: 0,
        },
        interactive: true,
      };

    case 'cascade':
      return {
        style: {
          position: 'absolute',
          inset: 'auto',
          top: cascadeOffset,
          left: cascadeOffset,
          width: `calc(100% - ${cascadeOffset}px)`,
          height: `calc(100% - ${cascadeOffset}px)`,
          zIndex: isActive ? 30 : 20 - index,
        },
        interactive: true,
      };

    // tabs: every window fills the shared frame; visibility of the
    // document body is handled by the inactive CSS. Inactive windows must
    // stay beneath the active one: they are absolutely positioned in the
    // same spot, and with equal z-index the LAST mounted window (the most
    // recently opened document) would stack ON TOP of the active window
    // after a tab switch, hiding it.
    case 'tabs':
    default:
      return {
        style: { zIndex: isActive ? 10 : 0 },
        interactive: true,
      };
  }
};
